
import { useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Trend, Language, AIService, SearchRegion, SearchTimeframe } from '../types';
import { CACHE_TIMING } from '../constants/uiConfig';
import { DEFAULT_SEARCH_CONFIG } from '../constants/searchConfig';

export const useTrendEngine = (aiService: AIService, language: Language) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useState<{
    niche: string; 
    region: SearchRegion; 
    timeframe: SearchTimeframe;
    deepMode: boolean; 
    image?: string;
  } | null>(null);

  // Use React Query for fetching trends
  const { data: trends = [], refetch, isFetching, error } = useQuery({
    queryKey: ['marketTrends', searchParams?.niche, searchParams?.region, searchParams?.timeframe, searchParams?.deepMode, searchParams?.image, language],
    queryFn: async () => {
      if (!searchParams) return [];
      return await aiService.fetchMarketTrends(
        searchParams.niche, 
        language, 
        searchParams.region, 
        searchParams.timeframe,
        searchParams.deepMode,
        searchParams.image
      );
    },
    enabled: false, // Triggered manually via fetchTrends
    staleTime: CACHE_TIMING.TRENDS_STALE_TIME,
    gcTime: CACHE_TIMING.TRENDS_GC_TIME,
  });

  const fetchTrends = useCallback(async (searchTerm: string, region: SearchRegion = DEFAULT_SEARCH_CONFIG.REGION, timeframe: SearchTimeframe = DEFAULT_SEARCH_CONFIG.TIMEFRAME, deepMode: boolean = DEFAULT_SEARCH_CONFIG.DEEP_MODE, image?: string) => {
    setSearchParams({ niche: searchTerm, region, timeframe, deepMode, image });
    
    const result = await queryClient.fetchQuery({
      queryKey: ['marketTrends', searchTerm, region, timeframe, deepMode, image, language],
      queryFn: () => aiService.fetchMarketTrends(searchTerm, language, region, timeframe, deepMode, image),
      staleTime: CACHE_TIMING.TRENDS_STALE_TIME
    });
    
    return result;
  }, [aiService, language, queryClient]);

  // Support manual hydration from IDB or Deep Dive updates
  const setTrends = useCallback((newTrends: Trend[] | ((prev: Trend[]) => Trend[])) => {
    if (typeof newTrends === 'function') {
        queryClient.setQueryData(['marketTrends', searchParams?.niche, searchParams?.region, searchParams?.timeframe, searchParams?.deepMode, searchParams?.image, language], (old: Trend[] | undefined) => {
            return newTrends(old || []);
        });
    } else {
        queryClient.setQueryData(['marketTrends', searchParams?.niche, searchParams?.region, searchParams?.timeframe, searchParams?.deepMode, searchParams?.image, language], newTrends);
    }
  }, [queryClient, searchParams, language]);

  const updateTrend = useCallback((index: number, updates: Partial<Trend>) => {
    setTrends(prev => {
      const newTrends = [...prev];
      if (newTrends[index]) {
        newTrends[index] = { ...newTrends[index], ...updates };
      }
      return newTrends;
    });
  }, [setTrends]);

  const analyzeTrendDeepDive = useCallback(async (index: number) => {
    // Access current data synchronously from cache or hook
    const currentTrends = queryClient.getQueryData<Trend[]>(['marketTrends', searchParams?.niche, searchParams?.region, searchParams?.timeframe, searchParams?.deepMode, searchParams?.image, language]) || trends;
    const trend = currentTrends[index];
    
    if (!trend) return;
    if (trend.deepDive) return;

    try {
      const data = await aiService.getTrendDeepDive(trend.title, searchParams?.niche || '', language);
      updateTrend(index, { deepDive: data });
    } catch (error) {
      console.error("Deep dive analysis failed:", error);
      throw error;
    }
  }, [aiService, language, queryClient, searchParams, trends, updateTrend]);

  const clearTrends = useCallback(() => {
    setSearchParams(null);
    queryClient.removeQueries({ queryKey: ['marketTrends'] });
  }, [queryClient]);

  const setSearchContext = useCallback((niche: string, region: SearchRegion, timeframe: SearchTimeframe, deepMode: boolean, image?: string) => {
    setSearchParams({ niche, region, timeframe, deepMode, image });
  }, []);

  return useMemo(() => ({
    niche: searchParams?.niche || '',
    region: searchParams?.region || DEFAULT_SEARCH_CONFIG.REGION,
    timeframe: searchParams?.timeframe || DEFAULT_SEARCH_CONFIG.TIMEFRAME,
    deepMode: searchParams?.deepMode ?? DEFAULT_SEARCH_CONFIG.DEEP_MODE,
    image: searchParams?.image,
    setNiche: (n: string) => setSearchParams(prev => ({
      ...prev,
      niche: n,
      region: prev?.region || DEFAULT_SEARCH_CONFIG.REGION,
      timeframe: prev?.timeframe || DEFAULT_SEARCH_CONFIG.TIMEFRAME,
      deepMode: prev?.deepMode ?? DEFAULT_SEARCH_CONFIG.DEEP_MODE,
      image: prev?.image
    })),
    setSearchContext, // Helper for bulk restoration
    trends,
    setTrends,
    fetchTrends,
    updateTrend,
    analyzeTrendDeepDive,
    clearTrends,
    isLoading: isFetching,
    error
  }), [
    searchParams,
    setSearchContext,
    trends,
    setTrends,
    fetchTrends,
    updateTrend,
    analyzeTrendDeepDive,
    clearTrends,
    isFetching,
    error
  ]);
};
